<template>
  <div class="enhancify grid-module marketing-materials-themes">
    <h3 class="settings-text--gray">
      Manage theme
    </h3>

    <div class="columns">
      <div class="column is-7">
        <div class="filter">
          <label>User:</label>
          <ModernSelect
            v-model="filters.userTypeId"
            optionLabelKey="value"
            optionValueKey="id"
            :options="userTypes"
          >
            <option
              v-for="(option, idx) in userTypes"
              :key="idx"
              :value="option.id"
            >
              <template v-if="option.isCustomerType">
                Customer:
              </template>
              {{ option.value }}
            </option>
          </ModernSelect>
        </div>
      </div>

      <div class="column is-5 buttons">
        <div class="buttons is-pulled-right">
          <b-button
            @click="onCancelClick"
            class="modern-button is-outlined"
          >
            cancel
          </b-button>

          <template v-if="checkAccess(MARKETING_MATERIALS_MANAGE_MODIFY)">
            <b-button
              class="modern-button"
              @click="onAddClick"
            >
              add theme
            </b-button>
          </template>
        </div>
      </div>
    </div>

    <p>
      Themes are used in the filters within the Social Media Images and Banners sections.
      <br>
      Use the “Pin to Top” feature to set which theme should appear first.
    </p>

    <div class="grid-module__grid">
      <b-table
        :data="themes"
        :loading="isTableLoading"
        sticky-header
        class="bordered-table"
        ref="tbl"
      >
        <template v-slot="props">
          <b-table-column
            label="Name"
            width="85%"
            field="name"
          >
            <template #header="column">
              <div class="enhtableth" :class=column.column.thclass>
                <span class="enhlabel">{{ column.column.label }}</span>
              </div>
            </template>

            <span class="cell__value">{{ props.row.name }}</span>
          </b-table-column>

          <b-table-column
            :visible="checkAccess(MARKETING_MATERIALS_MANAGE_MODIFY)"
            class="cell"
            width="15%"
            cell-class="action-data-column"
          >
            <div class="actions-wrapper">
              <span
                title="Pin to top"
                class="icon is-success action-button"
                @click="onPinClick(props.row)"
              >
                <i
                  class="fa-thumbtack fa-lg"
                  style="cursor: pointer"
                  :class="{ 'fas': props.row.isPinned, 'fal': !props.row.isPinned }"
                />
              </span>

              <span
                title="Delete theme"
                class="icon is-success action-button"
                @click="onRemoveClick(props.row)"
              >
                <i class="far fa-trash-alt fa-lg" />
              </span>
            </div>
          </b-table-column>
        </template>

        <template #empty>
          <div v-if="themes.length === 0" class="columns is-centered" style="margin: 0;">
            <div class="column is-half enh-disabled has-text-centered">
              <b-icon pack="fal" icon="frown" size="is-large"></b-icon>
              <p>Nothing here</p>
            </div>
          </div>
        </template>
      </b-table>
    </div>
  </div>
</template>

<script>

import { mapGetters, mapMutations } from 'vuex';
import * as MUTATIONS from '@/store/mutations';
import {
  MARKETING_MATERIALS_MANAGE_MODIFY,
} from '@/const/privileges';
import ModernSelect from '@/components/ModernSelect.vue';
import { CUSTOMER_TYPE_CONTRACTOR_ID } from '@/const/constants';
import {
  pin,
  remove,
  search,
  unpin,
} from '@/api/marketing_materials_theme';
import { get } from 'lodash';
import MarketingMaterialsThemeForm from './MarketingMaterialsThemeForm.vue';

export default {
  name: 'MarketingMaterialsThemes',
  components: { ModernSelect },

  data() {
    return {
      MARKETING_MATERIALS_MANAGE_MODIFY,
      isTableLoading: false,
      filters: {
        userTypeId: CUSTOMER_TYPE_CONTRACTOR_ID,
      },
      themes: [],
    };
  },

  computed: {
    ...mapGetters(['authAuthenticator', 'hasAccess']),
    ...mapGetters('constant', ['userTypes']),
  },

  async mounted() {
    await this.searchThemes();
  },

  watch: {
    'filters.userTypeId': function filtersUserTypeIdWatcher() {
      this.searchThemes();
    },
  },

  methods: {
    ...mapMutations({
      startLoading: MUTATIONS.START_LOADING,
      finishLoading: MUTATIONS.FINISH_LOADING,
    }),
    async searchThemes() {
      this.startLoading();
      this.isTableLoading = true;

      try {
        const response = await search({ userTypeId: this.filters.userTypeId });
        this.themes = response.data;
      } catch (error) {
        this.$buefy.snackbar.open({
          type: 'is-danger',
          message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
          position: 'is-top',
          indefinite: true,
          actionText: ' ',
        });
      }

      this.finishLoading();
      this.isTableLoading = false;
    },
    onCancelClick() {
      this.$emit('cancel');
    },
    async onAddClick() {
      const self = this;
      this.$buefy.modal.open({
        parent: this,
        component: MarketingMaterialsThemeForm,
        hasModalCard: true,
        trapFocus: true,
        scroll: 'keep',
        ariaModal: true,
        customClass: 'modern-fix-modal',
        canCancel: ['outside'],
        props: { userTypeId: Number(self.filters.userTypeId) },
        events: {
          async submit() {
            await self.searchThemes();
          },
        },
      });
    },
    async onPinClick(theme) {
      this.startLoading();

      const apiMethod = theme.isPinned ? unpin : pin;
      try {
        await apiMethod(theme.id);
        await this.searchThemes();
      } catch (error) {
        this.$buefy.snackbar.open({
          type: 'is-danger',
          message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
          position: 'is-top',
          indefinite: true,
          actionText: ' ',
        });
      }

      this.finishLoading();
    },
    async onRemoveClick(theme) {
      const self = this;
      this.$buefy.dialog.confirm({
        title: 'Deleting theme',
        message: 'Are you sure you want to <b>delete</b> this theme? This action cannot be undone.',
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        ariaModal: true,
        async onConfirm() {
          self.startLoading();

          try {
            await remove(theme.id);
            this.$buefy.toast.open({ message: 'Delete successful!', type: 'is-success', duration: 3000 });
            await self.searchThemes();
          } catch (error) {
            this.$buefy.snackbar.open({
              type: 'is-danger',
              message: get(error, 'response.data.message', 'An unexpected error occurred, please contact support@enhancify.com'),
              position: 'is-top',
              indefinite: true,
              actionText: ' ',
            });
          }

          self.finishLoading();
        },
      });
    },
    checkAccess(privilege) {
      return this.hasAccess(privilege, privilege, true);
    },
  },
};
</script>

<style lang="stylus">
.settings .marketing-materials .marketing-materials-themes
  max-width 720px
  &.grid-module
    height auto
    min-height none
  & h3
    font-size 20px

  & .filter
    padding 0
    display flex
    justify-content start
    align-items center
    & label
      font-weight 600
      margin-right 10px

  & .buttons
    & .button
      width 140px
      min-width: 110px
      height 48px
      text-transform uppercase

  & p
    margin-bottom 0.75rem
    font-size .9em
</style>

<style lang="stylus" scoped>
@import "~@/assets/style/views/Manage/Settings/table.styl";
</style>
